import { ShareableType } from '@bindoc-projects/resource-domain-lib';

import { BD_WORKSPACE_PATH } from '../../workspace-link/workspace-link.service';
import {
  ShareableDetailRoute,
  ShareableDetailRoutes,
} from './shareable-detail-routes';

/**
 * A folder has no page of its own. What reaches the user as a shareable — a
 * row in "Kürzlich verwendet", "In neuem Tab öffnen" on a shared folder — is a
 * folder shared with them, which lives in the grantor's tree. So it opens in
 * the workspace's "Geteilt" tab, browsed in place under "Geteilt › …"
 * (BDA-6454), in every meta-app alike.
 */
const WORKSPACE_FOLDER_ROUTE: ShareableDetailRoute = (id) => ({
  commands: [BD_WORKSPACE_PATH],
  extras: { queryParams: { tab: 'grants', folderId: id } },
});

/**
 * The BinDoc frontend that owns a shareable's detail view: the German
 * meta-app, its Austrian or its Swiss sibling.
 */
export type BdMetaAppId = 'de' | 'at' | 'ch';

/**
 * Maps the shareable types the German meta-app can surface (workspace folders,
 * global search, "Kürzlich verwendet"/grants) to the router command that opens
 * its detail view. Consumed via `BD_SHAREABLE_DETAIL_ROUTES` by
 * {@link BdShareableSelectService}.
 *
 * Klia's own types are NOT here: they are shared with the standalone Klia app
 * as `KLIA_SHAREABLE_DETAIL_ROUTES` and merged onto this map in the meta-app's
 * `main.ts`. Anything that resolves a *German* meta-app route from this map
 * must merge them too, or the three Klia types come back unroutable — the
 * administration console's `resource-app-link.service.ts` does, after shipping
 * once without it and showing no link on a Klia conversation.
 *
 * Each command array MUST resolve to a real route in the owning app's route
 * table — an unmatched path silently 404s when the user opens the shareable.
 *
 * The three maps live here rather than in each app's `main.ts` because the
 * administration app links *into* those apps ("In BinDoc öffnen") and cannot
 * import another app's sources. One copy is what keeps the admin links and the
 * apps' own navigation from drifting apart.
 *
 * The entry segments cannot be imported from the feature libs that serve them:
 * a feature's `routes.const.ts` statically imports its components, and this
 * file is reachable from `@bindoc-projects/resource-lib/registration`, which every
 * app loads at bootstrap — one such import would put those components into
 * every eager bundle. The first segment is an app-level decision anyway (each
 * app's own `app-routing.module.ts` / `app.routes.ts`), and a lib may not
 * import an app. `apps/meta-app/src/app/shareable-detail-routes-resolve.spec.ts` guards
 * that side instead: it fails when a registered route no longer starts at a
 * path the app serves. The AT/CH apps have no test target, so their (three and
 * six entry) maps carry no such guard.
 */
export const META_APP_SHAREABLE_DETAIL_ROUTES: ShareableDetailRoutes = new Map<
  ShareableType,
  ShareableDetailRoute
>([
  [ShareableType.FOLDER, WORKSPACE_FOLDER_ROUTE],
  [ShareableType.FILE, (id) => ({ commands: ['/user/files', id] })],
  [
    ShareableType.COMPETITOR_ANALYSIS,
    (id) => ({
      commands: ['/competitors-analysis', 'result'],
      extras: { queryParams: { appContentSnippetId: id } },
    }),
  ],
  [
    ShareableType.POTENTIAL_ANALYSIS,
    (id) => ({
      commands: ['/potential', 'analysis'],
      extras: { queryParams: { appContentSnippetId: id } },
    }),
  ],
  [
    ShareableType.MARKET_PROJECTION,
    (id) => ({
      commands: ['/projection', 'analysis'],
      extras: { queryParams: { appContentSnippetId: id } },
    }),
  ],
  [
    ShareableType.BUSINESS_CASE,
    (id) => ({
      commands: ['/business-case', 'analysis'],
      extras: { queryParams: { appContentSnippetId: id } },
    }),
  ],
  [
    ShareableType.CASE_EXPORT_TEMPLATE,
    (id) => ({
      commands: ['/virtual-clinics', 'case-export-templates', id],
    }),
  ],
  [
    ShareableType.CLINICS_FILTER,
    (id) => ({
      commands: ['/clinics-filters', id],
    }),
  ],
  [
    ShareableType.CLINICS_EXPORT_TEMPLATE,
    (id) => ({
      commands: ['/clinics-filters', 'export-templates', id],
    }),
  ],
  /*
   * `CLINIC_OPERATING_NUMBER_GROUP` is deliberately absent (BDA-5849): the
   * `/operating-numbers` feature was replaced by the Klinik-Cube and its route
   * removed WITHOUT a redirect, so the old
   * `/operating-numbers/clinics/user-defined/:id/edit` command now 404s. The
   * shareable type itself stays — it is still a named resource
   * ("Kennzahlengruppe") — it simply has no detail view to open any more, and
   * an entry here would hand the administration console's "In BinDoc öffnen"
   * a dead link. Re-add it once the Klinik-Cube serves an edit route for it.
   */
  [
    ShareableType.CODE_SPECTRUM,
    (id) => ({
      commands: ['/code-spectra', id],
    }),
  ],
  [ShareableType.MARKET, (id) => ({ commands: ['/markets', id] })],
  [
    ShareableType.SCENARIO_ANALYSIS,
    (id) => ({
      commands: ['/scenario-analysis', id],
    }),
  ],
  [
    ShareableType.SCENARIO_ANALYSIS_OPEN_CLOSE,
    (id) => ({
      commands: ['/demand-analysis', id],
    }),
  ],
  [
    ShareableType.SCENARIO_VV_ANALYSIS_OPEN_CLOSE,
    (id) => ({
      commands: ['/demand-analysis', 'vv', id],
    }),
  ],
  [
    ShareableType.VIRTUAL_CLINIC,
    (id) => ({
      commands: ['/virtual-clinics', id],
    }),
  ],
  [
    ShareableType.VIRTUAL_CLINIC_ANALYSIS_DASHBOARD,
    (id) => ({
      commands: ['/cube', 'dashboards', id],
    }),
  ],
  [
    // The Klinik-Cube (BDA-5849), the second cube this app hosts. Same
    // `/<tool>/dashboards/<id>` shape as the Cube above — see
    // AYS_CLINICS_CUBE_ROUTES.
    ShareableType.KENNZAHLEN_BENCHMARKING_DASHBOARD,
    (id) => ({
      commands: ['/clinics-cube', 'dashboards', id],
    }),
  ],
  [
    ShareableType.VIRTUAL_CLINIC_BENCHMARKING_GROUP,
    (id) => ({
      commands: ['/virtual-benchmarking-groups', id],
    }),
  ],
  [
    ShareableType.VIRTUAL_CLINIC_REFERRAL_IMPORT,
    (id) => ({
      // The import details route is nested: /virtual-clinic-referral/import/:id
      // (see AYS_VIRTUAL_CLINIC_REFERRAL_ROUTES). The flat
      // '/virtual-clinic-referral-import' path matches no route → 404. The
      // details component derives the virtualClinicId from the fetched entity,
      // so the import id alone is enough to open it (BDA).
      commands: ['/virtual-clinic-referral', 'import', id],
    }),
  ],
]);

/**
 * The Austrian meta-app's detail routes. See
 * {@link META_APP_SHAREABLE_DETAIL_ROUTES}.
 */
export const META_AT_APP_SHAREABLE_DETAIL_ROUTES: ShareableDetailRoutes =
  new Map<ShareableType, ShareableDetailRoute>([
    [ShareableType.FOLDER, WORKSPACE_FOLDER_ROUTE],
    [
      ShareableType.AT_VIRTUAL_CLINIC,
      (id) => ({
        commands: ['/virtual-clinics', id],
      }),
    ],
    [
      ShareableType.CODE_SPECTRUM,
      (id) => ({
        commands: ['/code-spectra', id],
      }),
    ],
    [
      ShareableType.AT_CUBE_DASHBOARD,
      (id) => ({
        commands: ['/cube', 'dashboards', id],
      }),
    ],
  ]);

/**
 * The Swiss meta-app's detail routes. See
 * {@link META_APP_SHAREABLE_DETAIL_ROUTES}.
 */
export const META_CH_APP_SHAREABLE_DETAIL_ROUTES: ShareableDetailRoutes =
  new Map<ShareableType, ShareableDetailRoute>([
    [ShareableType.FOLDER, WORKSPACE_FOLDER_ROUTE],
    [
      ShareableType.CH_VIRTUAL_CLINIC,
      (id) => ({
        commands: ['/virtual-clinics', id],
      }),
    ],
    [
      ShareableType.CASE_EXPORT_TEMPLATE,
      (id) => ({
        commands: ['/virtual-clinics', 'case-export-templates', id],
      }),
    ],
    [
      ShareableType.CODE_SPECTRUM,
      (id) => ({
        commands: ['/code-spectra', id],
      }),
    ],
    [
      ShareableType.CH_CUBE_DASHBOARD,
      (id) => ({
        commands: ['/cube', 'dashboards', id],
      }),
    ],
    [
      ShareableType.VIRTUAL_CLINIC_BENCHMARKING_GROUP,
      (id) => ({
        commands: ['/virtual-benchmarking-groups', id],
      }),
    ],
    [ShareableType.FILE, (id) => ({ commands: ['/user/files', id] })],
  ]);

/**
 * Every meta-app's detail routes, keyed by the app that serves them. A Map
 * rather than an object literal so a lookup is a `.get()` call instead of
 * dynamic key access, which Codacy flags.
 */
export const BD_META_APP_DETAIL_ROUTES: ReadonlyMap<
  BdMetaAppId,
  ShareableDetailRoutes
> = new Map<BdMetaAppId, ShareableDetailRoutes>([
  ['de', META_APP_SHAREABLE_DETAIL_ROUTES],
  ['at', META_AT_APP_SHAREABLE_DETAIL_ROUTES],
  ['ch', META_CH_APP_SHAREABLE_DETAIL_ROUTES],
]);

/** The shareable types only the Austrian app serves. */
const AT_ONLY_TYPES: ReadonlySet<ShareableType> = new Set([
  ShareableType.AT_VIRTUAL_CLINIC,
  ShareableType.AT_CUBE_DASHBOARD,
]);

/** The shareable types only the Swiss app serves. */
const CH_ONLY_TYPES: ReadonlySet<ShareableType> = new Set([
  ShareableType.CH_VIRTUAL_CLINIC,
  ShareableType.CH_CUBE_DASHBOARD,
]);

/**
 * The app a shareable belongs to, derived from its type — the only signal
 * there is, since `ShareableObj` carries no country.
 *
 * Only the country apps' own types are unambiguous. Everything else resolves
 * to the German meta-app, including the four types the country apps *also*
 * serve (`code-spectrum`, `file`, `case-export-template`,
 * `virtual-clinic-benchmarking-group`) — for those, DE is a best guess, not a
 * fact.
 */
export function bdMetaAppIdForShareableType(type: ShareableType): BdMetaAppId {
  if (AT_ONLY_TYPES.has(type)) {
    return 'at';
  }
  if (CH_ONLY_TYPES.has(type)) {
    return 'ch';
  }
  return 'de';
}
